// @flow
import React, { Component, Fragment } from "react";
import { isEqual, noop, get, flowRight as compose } from "lodash";
import type { Dispatch } from "redux";
import { connect } from "react-redux";
import { graphql } from "@apollo/client/react/hoc";
import ApolloContext from "@tvg/utils/apolloContext";
import mediator from "@tvg/mediator";
import type {
  RaceCardRunner,
  PastRace,
  RaceOdds,
  RaceCardBettingInterest
} from "@tvg/types/Race";
import type { TabKey } from "@tvg/atomic-ui/_templates/RaceOfficials";
import type {
  FullResultRunner,
  PastPerformanceRace
} from "@fdr/types/PastPerformance";
import type { NullaryFn, UnaryFn } from "@tvg/types/Functional";
import type { Device } from "@tvg/types/Device";
import sortRunnerList from "@tvg/utils/handicappingSort";
import {
  findRunnerInRace,
  getStreamSrc
} from "@tvg/utils/pastPerformanceUtils";
import RunnerInfo from "@fdr/program-page-runner-info";
import BetRaceLink from "@fdr/atomic-ui/_molecules/BetRaceLink";
import convertBettingInterests from "@fdr/utils/pastPerformanceUtils";
import RaceResults from "@fdr/atomic-ui/_organisms/RaceResults";
import RaceRunnersPayoffs from "@fdr/atomic-ui/_organisms/RaceRunnersPayoffs";
import RacePayoffs from "@fdr/atomic-ui/_organisms/RacePayoffs";
import { setResultsOfficialsTab } from "@tvg/shared-actions/BetActions";
import { fixedOffset } from "@fdr/utils/RaceHeaderUtils";
import ProgramPageTabs from "@fdr/atomic-ui/_organisms/ProgramPageTabs";
import RaceBets from "@fdr/atomic-ui/_organisms/RaceBets";
import { emptyMessageGtmCallback } from "@fdr/program-page";
import RaceCardTemplate from "@fdr/atomic-ui/_templates/RaceCard";

import InlinePastPerformance from "@fdr/atomic-ui/_templates/InlinePastPerformance";
import HorsePastPerformances from "@fdr/atomic-ui/_templates/HorsePastPerformances";
import ModalColumns from "@tvg/atomic-ui/_static/BaseComponents/ModalColumns/";

import type { RaceBet } from "@fdr/atomic-ui/_templates/MyBets/types";
import { getAccountNumber } from "@urp/store-selectors";
import HorsePerformanceQuery from "../graphql/queries/horsePastPerformance.graphql";
import WagerQuery from "../graphql/queries/wagerQuery.graphql";
import { ApolloOptionsPP, ApolloOptionsWager } from "../graphql/options.graph";

type Props = {
  dispatch: Dispatch<*>,
  device: Device,
  runnerName: string,
  selectedRace: PastPerformanceRace,
  selectedRaceIndex: number,
  races: PastPerformanceRace[],
  pastRace: ?PastRace,
  fullResults: FullResultRunner[],
  hasTimeformData: boolean,
  hasNextRace: boolean,
  hasPreviousRace: boolean,
  onPreviousRaceNavigation: NullaryFn<void>,
  onNextRaceNavigation: NullaryFn<void>,
  onRaceFromList: UnaryFn<number, void>,
  hasNavigation: boolean,
  videoFullscreenTilt: boolean,
  isRaceListView: boolean,
  onClickGTMEvent: UnaryFn<RaceCardRunner, void>,
  isLoading: boolean,
  updateRunner: UnaryFn<RaceCardRunner, void>,
  accountNumber: string,
  raceBets: ?(RaceBet[]),
  onMatchConditions: NullaryFn<void>,
  isMatchConditionsOn: boolean,
  originRace: string,
  hasWebSockets: boolean,
  wagerProfile: string,
  isLogged: boolean,
  emptyBetsMessage: string,
  isBettingModalOpen?: boolean,
  showSilks: boolean,
  bettingInterests: RaceCardBettingInterest[][],
  showRunnerUpdate: boolean,
  enableNewRacePage: boolean,
  selectAllButtonLabel?: string
};

type State = {
  replaySrc: ?string,
  selectedTab: string,
  selectedRunners: string[][]
};

export class PastPerformanceComponent extends Component<Props, State> {
  static defaultProps = {
    dispatch: noop,
    device: "mobile",
    races: [],
    fullResults: [],
    runnerName: "",
    selectedRaceIndex: -1,
    pastRace: null,
    hasTimeformData: false,
    hasPreviousRace: false,
    hasNextRace: false,
    onPreviousRaceNavigation: noop,
    onNextRaceNavigation: noop,
    onRaceFromList: noop,
    hasNavigation: false,
    videoFullscreenTilt: false,
    isRaceListView: false,
    onClickGTMEvent: noop,
    isLoading: true,
    updateRunner: noop,
    accountNumber: "",
    raceBets: null,
    onMatchConditions: noop,
    isMatchConditionsOn: false,
    originRace: "",
    hasWebSockets: false,
    wagerProfile: "FDR-Generic",
    isLogged: false,
    emptyBetsMessage: "",
    isBettingModalOpen: false,
    showSilks: false,
    bettingInterests: [],
    showRunnerUpdate: false,
    enableNewRacePage: false,
    selectAllButtonLabel: "Box"
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      replaySrc: null,
      selectedTab: "Result",
      selectedRunners: []
    };
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    const {
      runnerName,
      selectedRace,
      selectedRaceIndex,
      pastRace,
      hasPreviousRace,
      hasNextRace,
      isLoading,
      isMatchConditionsOn,
      raceBets
    } = this.props;

    return (
      isMatchConditionsOn !== nextProps.isMatchConditionsOn ||
      runnerName.toLowerCase() !== nextProps.runnerName.toLowerCase() ||
      hasPreviousRace !== nextProps.hasPreviousRace ||
      hasNextRace !== nextProps.hasNextRace ||
      isLoading !== nextProps.isLoading ||
      selectedRaceIndex !== nextProps.selectedRaceIndex ||
      (nextState && nextState.replaySrc !== this.state.replaySrc) ||
      !isEqual(nextProps.raceBets, raceBets) ||
      !isEqual(selectedRace, nextProps.selectedRace) ||
      !isEqual(pastRace, nextProps.pastRace) ||
      nextState.selectedTab !== this.state.selectedTab ||
      !isEqual(nextState.selectedRunners, this.state.selectedRunners)
    );
  }

  componentDidUpdate(prevProps: Props) {
    const runner = this.getRunner();

    if (runner) {
      this.props.updateRunner(runner);
    }

    if (this.props.pastRace && !prevProps.pastRace) {
      // CLICKED ON A PP
      if (runner) {
        this.props.onClickGTMEvent(runner);
      }
    }

    if (
      this.props.selectedRace &&
      this.props.selectedRace.hasReplays &&
      (!this.state.replaySrc ||
        !isEqual(this.props.selectedRace, prevProps.selectedRace))
    ) {
      const replayFileName = get(this.props.selectedRace, "replayFileName");
      getStreamSrc(replayFileName).then((data) => {
        if (data.payload && data.type === "success") {
          this.setState({
            replaySrc: get(data, "payload")
          });
        }
      });
    }
  }

  getRunner = (): ?RaceCardRunner => {
    const { runnerName, pastRace } = this.props;
    return pastRace
      ? findRunnerInRace(runnerName, pastRace.bettingInterests)
      : null;
  };

  getTabs = () => [
    {
      title: "Result",
      tag: { isVisible: !this.props.hasTimeformData, text: "Unofficial" },
      isActive: this.state.selectedTab === "Result",
      onClick: (tab: string) => {
        this.setState({ selectedTab: tab });
        // $FlowFixMe
        this.changeRaceOfficialsTab(tab.toLowerCase());
      }
    },
    {
      title: "Bets",
      isActive: this.state.selectedTab === "Bets",
      onClick: (tab: string) => {
        this.setState({ selectedTab: tab });
        // $FlowFixMe
        this.changeRaceOfficialsTab(tab.toLowerCase());
      }
    },
    {
      title: "Racecard",
      isActive: this.state.selectedTab === "Racecard",
      onClick: (tab: string) => {
        this.setState({ selectedTab: tab });
        // $FlowFixMe
        this.changeRaceOfficialsTab(tab.toLowerCase());
      }
    }
  ];

  getCurrentOdds = (currentOdds: RaceOdds) =>
    currentOdds.numerator === 0 && currentOdds.denominator === 0
      ? null
      : currentOdds;

  changeRaceOfficialsTab = (tab: TabKey) => {
    switch (tab) {
      case "result":
        mediator.base.dispatch({
          type: "PAST_PERFORMANCES:VIEW_RESULTS",
          payload: {
            trackName: get(this.props.selectedRace, "track.name", ""),
            runnerName: get(this.props, "runnerName", ""),
            raceNumber: get(this.props.selectedRace, "raceNumber", "")
          }
        });
        break;
      case "racecard":
        mediator.base.dispatch({
          type: "PAST_PERFORMANCES:VIEW_RACECARD",
          payload: {
            trackName: get(this.props.selectedRace, "track.name", ""),
            runnerName: get(this.props, "runnerName", ""),
            raceNumber: get(this.props.selectedRace, "raceNumber", "")
          }
        });
        break;
      case "bets":
        mediator.base.dispatch({
          type: "PAST_PERFORMANCES:VIEW_BETS",
          payload: {
            trackName: get(this.props.selectedRace, "track.name", ""),
            runnerName: get(this.props, "runnerName", ""),
            raceNumber: get(this.props.selectedRace, "raceNumber", "")
          }
        });
        break;
      default:
        break;
    }

    this.props.dispatch(setResultsOfficialsTab(tab));
    return true;
  };

  sendVideoGtmEvent = () => {
    mediator.base.dispatch({
      type: "PAST_PERFORMANCES:VIEW_REPLAY",
      payload: {
        trackName: get(this.props, "selectedRace.track.name"),
        runnerName: this.props.runnerName,
        raceNumber: get(this.props, "selectedRace.number")
      }
    });
  };

  deviceLockRotationToggle = (lock: boolean = true) => {
    if (this.props.videoFullscreenTilt) {
      mediator.ios.dispatch({
        type: "IPHONE_LOCK_SCREEN_ROTATION",
        payload: { lockScreenRotation: lock ? 0 : 2 }
      });
    }
  };

  changeUpdate = (runnerId: string, leg: number, isOpen: boolean) => {
    const newState = [...this.state.selectedRunners];
    newState[leg] = isOpen
      ? // $FlowFixMe
        this.state.selectedRunners[leg]?.filter((item) => item !== runnerId) ??
        []
      : [...(newState[leg] ?? []), runnerId];

    this.setState({
      selectedRunners: newState
    });
  };

  expandedInfo = (
    runnerId: number,
    leg: number,
    isNumberFireSection: boolean
  ) => {
    const runner = this.props.bettingInterests[leg].find((bettingInterest) =>
      // $FlowFixMe
      bettingInterest?.runners?.find(
        (runnerItem) => runnerItem.runnerId === runnerId
      )
    ) ?? { runners: [] };

    const hasOfficialResults =
      get(this.props.selectedRace, "status.code", "") === "RO";
    return (
      <ApolloContext.Consumer>
        {(value) => (
          <RunnerInfo
            hasWebSockets={this.props.hasWebSockets}
            runnerId={runnerId}
            wagerProfile={this.props.wagerProfile}
            fcpClient={get(value, "fcpClient")}
            runner={runner}
            betLeg={leg}
            bettingInterests={this.props.raceBets}
            race={this.props.selectedRace}
            openHelpModal={noop}
            isNumberFireSection={isNumberFireSection}
            showWinProb={!hasOfficialResults}
            showPastRaces={false}
          />
        )}
      </ApolloContext.Consumer>
    );
  };

  renderResultTab() {
    const payoffBets = ["WN", "PL", "SH"];
    const results = {
      payoffs: (get(this.props.pastRace, "results.payoffs") || []).filter(
        (payoff) => payoffBets.indexOf(payoff.wagerType.code) === -1
      ),
      runners: get(this.props.pastRace, "results.runners") || []
    };

    const winPlaceShowPayoffs = get(
      this.props.pastRace,
      "results.payoffs",
      []
    ).filter((payoff) => payoffBets.some((c) => c === payoff.wagerType.code));

    const {
      hasTimeformData,
      selectedRace,
      runnerName,
      fullResults,
      showSilks,
      enableNewRacePage
    } = this.props;

    const biData = convertBettingInterests(
      get(this.props.pastRace, "bettingInterests") || []
    );

    return (
      <Fragment>
        <RaceResults
          resultsType={hasTimeformData ? "full" : "basic"}
          runnerName={runnerName}
          runners={fullResults}
          scratchedRunners={biData.scratchedRunners}
          raceTypeCode={get(selectedRace, "raceTypeCode")}
          showSilks={showSilks}
          enableNewRacePage={enableNewRacePage}
          bettingInterests={this.props.bettingInterests}
          isRunnerExpandable={false}
          selectAllButtonLabel={this.props.selectAllButtonLabel}
        />
        <RaceRunnersPayoffs
          raceTypeCode={get(selectedRace, "raceTypeCode")}
          key="results-tab-runners"
          runners={get(results, "runners", [])}
          hasBottomBorder={false}
          displayRunner={false}
          payoffs={winPlaceShowPayoffs}
          showSilks={showSilks}
        />
        <RacePayoffs
          hasTitle={false}
          key="results-tab-payoffs"
          payoffs={get(results, "payoffs", [])}
        />
      </Fragment>
    );
  }

  renderComponent = () => {
    const { selectedRace, showSilks, enableNewRacePage } = this.props;
    const biData = convertBettingInterests(
      get(this.props.pastRace, "bettingInterests") || []
    );

    switch (this.state.selectedTab) {
      case "Bets": {
        return (
          <Fragment>
            {get(this.props, "raceBets.length") > 0 && (
              <BetRaceLink
                showPastRace
                postTime={selectedRace.date}
                trackName={get(selectedRace, "track.name")}
                trackAbbr={get(selectedRace, "track.code")}
                raceNumber={selectedRace.raceNumber}
                isLink={false}
                showRaceDate
                closePreviousModal={noop}
                isGreyhound={false}
              />
            )}
            <RaceBets
              showRace={false}
              showSimpleEmptyMessage={false}
              emptyMessageCallback={emptyMessageGtmCallback}
              emptyMessage={{
                body: this.props.emptyBetsMessage,
                title: "",
                nextRaceGtm: {
                  buttonText: "",
                  module: "",
                  destinationUrl: ""
                },
                allTracksGtm: {
                  buttonText: "",
                  module: "",
                  destinationUrl: ""
                }
              }}
              raceNumber={get(this.props, "selectedRace.number")}
              bets={this.props.raceBets}
            />
          </Fragment>
        );
      }
      case "Result": {
        return this.renderResultTab();
      }
      default: {
        const sortedBettingInterest = sortRunnerList(
          get(biData, "bettingInterests", []),
          "basic",
          "saddlecloth"
        ).map((bi) => ({
          ...bi,
          currentOdds: this.getCurrentOdds(bi.currentOdds)
        }));

        return (
          <RaceCardTemplate
            updatePicks={false}
            hasRequested
            racesData={[]}
            raceTypeCode={get(selectedRace, "raceTypeCode", "T")}
            isLogged={this.props.isLogged}
            selectedLeg={0}
            isWagerable={false}
            // $FlowFixMe
            bettingInterests={[sortedBettingInterest]}
            race={selectedRace}
            races={[]}
            numColumns={1}
            showWinProb={false}
            isResulted
            isBettingModalOpen={this.props.isBettingModalOpen}
            showSilks={showSilks}
            enableNewRacePage={enableNewRacePage}
            showRunnerUpdate={this.props.showRunnerUpdate}
            expandedInfo={this.expandedInfo}
            changeUpdate={this.changeUpdate}
            selectedRunners={this.state.selectedRunners}
          />
        );
      }
    }
  };

  renderTabs = () => (
    <ProgramPageTabs
      isSticky={false}
      fixedOffset={fixedOffset(false)}
      tabs={this.getTabs()}
      withVideo={false}
    />
  );

  renderRace = () => {
    const payoffBets = ["WN", "PL", "SH"];
    const results = {
      payoffs: (get(this.props.pastRace, "results.payoffs") || []).filter(
        (payoff) => payoffBets.indexOf(payoff.wagerType.code) === -1
      ),
      runners: get(this.props.pastRace, "results.runners") || []
    };
    const biData = convertBettingInterests(
      get(this.props.pastRace, "bettingInterests") || []
    );

    return (
      <HorsePastPerformances
        results={results}
        race={this.props.selectedRace}
        runner={this.getRunner()}
        hasPreviousRace={this.props.hasNavigation && this.props.hasPreviousRace}
        hasNextRace={this.props.hasNavigation && this.props.hasNextRace}
        onPreviousRaceNavigation={this.props.onPreviousRaceNavigation}
        onNextRaceNavigation={this.props.onNextRaceNavigation}
        isLoading={this.props.isLoading}
        withBorders={this.props.device !== "mobile"}
        bettingInterests={biData.bettingInterests}
        scratchedRunners={biData.scratchedRunners}
        raceBets={this.props.raceBets || []}
        replaySrc={this.state.replaySrc}
        sendVideoGtmEvent={this.sendVideoGtmEvent}
        fullResults={this.props.fullResults}
        hasTimeformData={this.props.hasTimeformData}
        videoFullscreenTilt={this.props.videoFullscreenTilt}
        deviceLockRotationToggle={this.deviceLockRotationToggle}
        renderTabs={this.renderTabs()}
        selectedTab={this.state.selectedTab}
        renderComponent={this.renderComponent()}
      />
    );
  };

  renderList = () => (
    <InlinePastPerformance
      races={this.props.races}
      selectedRace={this.props.selectedRaceIndex}
      onRace={this.props.onRaceFromList}
      paddingless
      onMatchToggle={this.props.onMatchConditions}
      isMatchConditionsOn={this.props.isMatchConditionsOn}
      originRace={this.props.originRace}
    />
  );

  renderMobile = () => {
    if (this.props.isRaceListView) {
      return this.renderList();
    }

    return this.renderRace();
  };

  renderNotMobile = () => (
    <ModalColumns.GridHolder>
      <ModalColumns.Grid>
        <ModalColumns.Column side="left">
          {this.renderList()}
        </ModalColumns.Column>
        <ModalColumns.Column side="right">
          {this.renderRace()}
        </ModalColumns.Column>
      </ModalColumns.Grid>
    </ModalColumns.GridHolder>
  );

  render() {
    return this.props.device === "mobile" || !this.props.hasNavigation
      ? this.renderMobile()
      : this.renderNotMobile();
  }
}

export default connect(
  /* istanbul ignore next */
  (store) => ({
    emptyBetsMessage: get(store, "capi.messages.emptyBetsMessage"),
    accountNumber: getAccountNumber(store),
    hasNavigation: get(
      store,
      "capi.featureToggles.inLinePastPerformanceNavigation",
      false
    ),
    videoFullscreenTilt: get(
      store,
      "capi.featureToggles.videoFullscreenTilt",
      false
    ),
    wagerProfile: get(store, "userData.user.profile", "FDR-Generic"),
    isLogged: get(store, "userData.logged", false),
    isBettingModalOpen: get(store, "RaceProgram.startBetting"),
    showSilks: get(store, "capi.featureToggles.showSilks", false),
    enableNewRacePage: get(
      store,
      "capi.featureToggles.enableNewRacePage",
      false
    ),
    bettingInterests: get(
      store,
      "RaceProgram.currentRace.bettingInterests",
      []
    ),
    selectAllButtonLabel: get(store, "capi.messages.selectAllButtonLabel"),
    behgToggle: get(store, "capi.featureToggles.myBetsBehgConnection", false)
  }),
  /* istanbul ignore next */
  (dispatch: Dispatch<*>) => ({
    dispatch
  })
)(
  compose(
    graphql(HorsePerformanceQuery, ApolloOptionsPP),
    graphql(WagerQuery, ApolloOptionsWager)
  )(PastPerformanceComponent)
);
